import { useEffect, useRef } from 'react';
import { theme } from '@frameio/vapor-theme';

type UseHighlightOverlayOptions = {
  /** Element to follow with the highlight overlay */
  targetElement: HTMLElement | null;
  /** Whether the overlay should be active/visible */
  active: boolean;
  /** Class name applied to the overlay element */
  className: string;
  /** Class name for the visible state */
  visibleClassName: string;
  /** Whether the target is visible enough to show; shared with the tooltip. */
  isTargetVisible: boolean;
};

// Matches the overlay's CSS `transition-duration` so we unmount after the fade.
const HIDE_TRANSITION_MS = parseInt(theme.transitions.durations[200], 10);

/**
 * Creates and manages a DOM overlay that highlights and follows a target element.
 *
 * The overlay is positioned from the target's bounding rect and re-checked every
 * animation frame, so it stays locked on even when the target moves for reasons
 * that emit no scroll or resize event — e.g. a top bar or banner being inserted
 * above the app and reflowing the layout underneath it. It's a body-level
 * `position: fixed` element (never clipped by the target's container); it fades
 * out via `isTargetVisible` instead.
 */
export function useHighlightOverlay(options: UseHighlightOverlayOptions) {
  const {
    targetElement,
    active,
    className,
    visibleClassName,
    isTargetVisible,
  } = options;

  // Read in the rAF loop without re-running the effect (which restarts the fade).
  const isTargetVisibleRef = useRef(isTargetVisible);
  useEffect(() => {
    isTargetVisibleRef.current = isTargetVisible;
  }, [isTargetVisible]);

  useEffect(() => {
    if (!active || !targetElement) return undefined;

    const overlay = document.createElement('div');
    overlay.className = className;
    overlay.style.display = 'none';
    document.body.appendChild(overlay);

    let rafId: number | null = null;
    let hideTimeoutId: number | null = null;
    let isVisible = false;
    let hasPositioned = false;
    let lastTop = NaN;
    let lastLeft = NaN;
    let lastWidth = NaN;
    let lastHeight = NaN;

    // Fade out, then remove from layout once the transition ends.
    const hide = () => {
      if (overlay.style.display === 'none' || hideTimeoutId != null) return;
      overlay.classList.remove(visibleClassName);
      isVisible = false;
      hideTimeoutId = window.setTimeout(() => {
        hideTimeoutId = null;
        overlay.style.display = 'none';
        hasPositioned = false;
      }, HIDE_TRANSITION_MS);
    };

    const update = () => {
      if (!targetElement.isConnected || !isTargetVisibleRef.current) {
        hide();
        return;
      }

      const rect = targetElement.getBoundingClientRect();
      if (!rect.width || !rect.height) {
        hide();
        return;
      }

      // Target is visible again — cancel any in-flight fade-out before repositioning.
      if (hideTimeoutId != null) {
        window.clearTimeout(hideTimeoutId);
        hideTimeoutId = null;
      }

      const top = Math.round(rect.top);
      const left = Math.round(rect.left);
      const width = Math.round(rect.width);
      const height = Math.round(rect.height);

      // Only touch the DOM when the box changed (or we're re-showing) to avoid
      // needless style writes on every frame.
      if (
        overlay.style.display === 'none' ||
        top !== lastTop ||
        left !== lastLeft ||
        width !== lastWidth ||
        height !== lastHeight
      ) {
        overlay.style.display = 'block';
        overlay.style.top = `${top}px`;
        overlay.style.left = `${left}px`;
        overlay.style.width = `${width}px`;
        overlay.style.height = `${height}px`;
        lastTop = top;
        lastLeft = left;
        lastWidth = width;
        lastHeight = height;
      }

      // Fade in one frame after the first successful positioning so the opacity
      // transition runs from the target's real location rather than the corner.
      if (!isVisible) {
        if (hasPositioned) {
          overlay.classList.add(visibleClassName);
          isVisible = true;
        } else {
          hasPositioned = true;
        }
      }
    };

    const tick = () => {
      update();
      rafId = window.requestAnimationFrame(tick);
    };

    rafId = window.requestAnimationFrame(tick);

    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      if (hideTimeoutId != null) window.clearTimeout(hideTimeoutId);
      overlay.remove();
    };
  }, [active, className, visibleClassName, targetElement]);
}
